import { useCallback, useEffect, useState } from "react";
import { CalendarCheck, CalendarX, Loader2, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";

export const AvailabilityHealthPanel = () => {
  const [d, setD] = useState(null);
  const [busy, setBusy] = useState("");

  const load = useCallback(async () => {
    setBusy("load");
    try {
      const { data } = await api.get("/admin/tank-water-health-check/availability-debug");
      setD(data);
    } catch (e) { toast.error(formatApiError(e)); }
    setBusy("");
  }, []);
  useEffect(() => { load(); }, [load]);

  const regenerate = async () => {
    setBusy("regen");
    try {
      const { data } = await api.post(
        "/admin/tank-water-health-check/availability-debug/regenerate");
      setD(data.report);
      toast.success(`${data.created} date${data.created === 1 ? "" : "s"} added · `
        + `${data.public_times} bookable times now`);
    } catch (e) { toast.error(formatApiError(e)); }
    setBusy("");
  };

  const ok = d?.diagnosis?.ok;
  return (
    <div className={`mt-4 rounded-xl border p-4 ${ok === false
      ? "border-rose-300 bg-rose-50" : "border-slate-200 bg-slate-50"}`}
      data-testid="admin-thc-availability-health">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h3 className="flex items-center gap-2 text-sm font-bold text-slate-900">
          {ok === false
            ? <CalendarX className="h-4 w-4 text-rose-600" />
            : <CalendarCheck className="h-4 w-4 text-emerald-600" />}
          Public booking calendar
        </h3>
        <div className="flex gap-2">
          <button onClick={load} disabled={!!busy} data-testid="admin-thc-availability-recheck"
            className="rounded-full border border-slate-300 bg-white px-3 py-1.5 text-[11px] font-bold text-slate-700 hover:bg-slate-50 disabled:opacity-50">
            {busy === "load" ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : "Re-check"}
          </button>
          <button onClick={regenerate} disabled={!!busy} data-testid="admin-thc-availability-regenerate"
            className="rounded-full bg-cyan-700 px-3 py-1.5 text-[11px] font-bold text-white hover:bg-cyan-800 disabled:opacity-50">
            <RefreshCw className={`mr-1 inline h-3.5 w-3.5 ${busy === "regen" ? "animate-spin" : ""}`} />
            Rebuild weekly dates
          </button>
        </div>
      </div>

      {!d ? (
        <p className="mt-2 text-xs text-slate-500">Checking…</p>
      ) : (
        <>
          <p className={`mt-2 text-sm font-bold ${ok === false ? "text-rose-800" : "text-slate-800"}`}
            data-testid="admin-thc-availability-headline">
            {d.diagnosis?.headline}
          </p>
          {d.diagnosis?.fix && (
            <p className="mt-1 text-xs text-slate-700">{d.diagnosis.fix}</p>
          )}
          {d.next_available && (
            <p className="mt-1 text-xs text-slate-600">
              Next: {d.next_available.date_display} · {d.next_available.arrival_window_display}
            </p>
          )}
          <dl className="mt-3 grid grid-cols-2 gap-x-4 gap-y-1 text-[11px] text-slate-600 sm:grid-cols-4">
            {[["Dates on file", d.counts.slots_total],
              ["Future & on", d.counts.active_future],
              ["With room", d.counts.with_spare_capacity],
              ["Offering times", d.counts.offering_times]].map(([k, v]) => (
                <div key={k}><dt className="font-bold text-slate-500">{k}</dt><dd>{v}</dd></div>
              ))}
          </dl>
          {!!d.excluded_reasons?.length && (
            <ul className="mt-3 space-y-1 text-[11px] text-slate-700">
              {d.excluded_reasons.map((r) => (
                <li key={r.reason}>
                  <b>{r.slots}</b> skipped — {r.means}
                </li>
              ))}
            </ul>
          )}
          {!!d.fully_booked?.length && (
            <div className="mt-3" data-testid="admin-thc-fully-booked-list">
              <p className="text-[11px] font-bold text-rose-700">Fully booked</p>
              <ul className="mt-1 flex flex-wrap gap-1">
                {d.fully_booked.map((fb, i) => (
                  <li key={`${fb.technician_id}-${fb.date}-${i}`}
                    data-testid={`admin-thc-fully-booked-badge-${fb.date}-${fb.technician_id}`}
                    className="inline-flex items-center rounded-full bg-rose-50 text-rose-700 border border-rose-200 text-[11px] px-2 py-0.5 font-semibold">
                    {fb.technician_name || fb.technician_id} · {fb.date}
                  </li>
                ))}
              </ul>
            </div>
          )}
          <p className="mt-3 text-[11px] text-slate-500">
            Notice {d.settings.min_notice_hours}h · horizon {d.settings.booking_horizon_days} days ·
            max {d.settings.max_jobs_per_tech_per_day}/tech/day · visit
            {" "}{d.settings.health_check_duration_minutes}+{d.settings.health_check_buffer_minutes} min ·
            calendar source {d.settings.availability_provider}
            {d.servicem8.error ? ` · ServiceM8 read failing: ${d.servicem8.error}` : ""}
          </p>
          {!!d.technicians_on_slots?.length && (
            <p className="mt-1 text-[11px] text-slate-500">
              Technicians on availability:{" "}
              {d.technicians_on_slots.map((t) => `${t.name}${t.usable ? "" : " (inactive!)"}`).join(", ")}
            </p>
          )}
        </>
      )}
    </div>
  );
};
